import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { createCachedObservable } from '../utils/cached-observable.util';
import type {
  UsersResponse,
  UsersBasicoResponse,
  UserListItem,
  UserWritePayload,
  UserSingleResponse,
  UsuariosQueryParams,
} from '../models/user.model';
import type { PartnersResponse } from '../models/partner.model';

@Injectable({ providedIn: 'root' })
export class UsersService {
  private readonly baseUrl = `${environment.apiUrl}/api`;

  private readonly agentesCache = createCachedObservable(() =>
    this.http.get<unknown>(`${this.baseUrl}/users/basico`).pipe(
      map((body) => ({ data: this.extractDataArray(body) as UsersBasicoResponse['data'] })),
    ),
  );

  private readonly partnersCache = createCachedObservable(() =>
    this.http.get<PartnersResponse>(`${this.baseUrl}/partners`),
  );

  constructor(private http: HttpClient) {}

  /** Normaliza cuerpo JSON: array en raíz o `{ data: [...] }`. */
  private extractDataArray(res: unknown): unknown[] {
    if (Array.isArray(res)) return res;
    if (res && typeof res === 'object' && 'data' in res) {
      const d = (res as { data: unknown }).data;
      return Array.isArray(d) ? d : [];
    }
    return [];
  }

  private toList(res: unknown): UserListItem[] {
    return this.extractDataArray(res) as UserListItem[];
  }

  /** Listado mínimo (selectores, filtros): GET /api/users/basico (caché en memoria). */
  getAgentes(): Observable<UsersBasicoResponse> {
    return this.agentesCache.get();
  }

  /** Listado paginado (configuración): GET /api/users */
  getUsuarios(params: UsuariosQueryParams = {}): Observable<UsersResponse> {
    let httpParams = new HttpParams();
    if (params.page != null) httpParams = httpParams.set('page', String(params.page));
    if (params.per_page != null) httpParams = httpParams.set('per_page', String(params.per_page));
    if (params.search?.trim()) httpParams = httpParams.set('search', params.search.trim());
    if (params.fecha_desde?.trim()) httpParams = httpParams.set('fecha_desde', params.fecha_desde.trim());
    if (params.fecha_hasta?.trim()) httpParams = httpParams.set('fecha_hasta', params.fecha_hasta.trim());
    if (params.id_rol != null && params.id_rol > 0) {
      httpParams = httpParams.set('id_rol', String(params.id_rol));
    }
    return this.http.get<UsersResponse>(`${this.baseUrl}/users`, { params: httpParams });
  }

  /** Detalle por id. GET /api/users/:id */
  getUsuario(id: number): Observable<UserListItem> {
    return this.http.get<UserSingleResponse | UserListItem>(`${this.baseUrl}/users/${id}`).pipe(
      map((res): UserListItem => {
        if (res != null && typeof res === 'object' && 'data' in res && res.data != null) {
          return res.data as UserListItem;
        }
        return res as UserListItem;
      }),
    );
  }

  /** Alta. POST /api/users */
  createUsuario(payload: UserWritePayload): Observable<UserListItem> {
    return this.http.post<UserSingleResponse | UserListItem>(`${this.baseUrl}/users`, payload).pipe(
      map((res): UserListItem => {
        if (res != null && typeof res === 'object' && 'data' in res && res.data != null) {
          return res.data as UserListItem;
        }
        return res as UserListItem;
      }),
    );
  }

  /** Actualización. PUT /api/users/:id */
  updateUsuario(id: number, payload: UserWritePayload): Observable<UserListItem> {
    return this.http.put<UserSingleResponse | UserListItem>(`${this.baseUrl}/users/${id}`, payload).pipe(
      map((res): UserListItem => {
        if (res != null && typeof res === 'object' && 'data' in res && res.data != null) {
          return res.data as UserListItem;
        }
        return res as UserListItem;
      }),
    );
  }

  /** Baja. DELETE /api/users/:id */
  deleteUsuario(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/users/${id}`);
  }

  /** Partners: GET /api/partners (caché en memoria). */
  getPartners(): Observable<PartnersResponse> {
    return this.partnersCache.get();
  }

  clearCatalogCache(): void {
    this.agentesCache.clear();
    this.partnersCache.clear();
  }
}
